import React from 'react';
import styled from 'styled-components';

interface Tab {
  id: string;
  label: string;
  icon?: React.ReactNode;
  badge?: number;
}

interface TabsProps {
  tabs: Tab[];
  activeTab: string;
  onChange: (tabId: string) => void;
  children?: React.ReactNode;
}

const TabsContainer = styled.div``;

const TabList = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xs};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  
  &::-webkit-scrollbar {
    display: none;
  }
  
  @media (max-width: 768px) {
    gap: 0;
    margin: 0 -${({ theme }) => theme.spacing.md};
    padding: 0 ${({ theme }) => theme.spacing.md};
  }
`;

const TabButton = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  color: ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.textSecondary};
  background: transparent;
  border: none;
  border-bottom: 2px solid ${({ $active, theme }) => $active ? theme.colors.primary : 'transparent'};
  margin-bottom: -1px;
  white-space: nowrap;
  transition: all ${({ theme }) => theme.transitions.fast};
  cursor: pointer;
  flex-shrink: 0;
  
  &:hover {
    color: ${({ theme }) => theme.colors.text};
  }
  
  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
    font-size: ${({ theme }) => theme.typography.fontSizes.xs};
    gap: ${({ theme }) => theme.spacing.xs};
  }
`;

const TabBadge = styled.span`
  padding: 2px 8px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ theme }) => theme.colors.primaryLight};
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  
  @media (max-width: 768px) {
    padding: 1px 6px;
    font-size: 10px;
  }
`;

const TabContent = styled.div`
  padding-top: ${({ theme }) => theme.spacing.lg};
  
  @media (max-width: 768px) {
    padding-top: ${({ theme }) => theme.spacing.md};
  }
`;

export const Tabs: React.FC<TabsProps> = ({ tabs, activeTab, onChange, children }) => {
  return (
    <TabsContainer>
      <TabList>
        {tabs.map(tab => (
          <TabButton
            key={tab.id}
            $active={activeTab === tab.id}
            onClick={() => onChange(tab.id)}
          >
            {tab.icon}
            {tab.label}
            {tab.badge !== undefined && <TabBadge>{tab.badge}</TabBadge>}
          </TabButton>
        ))}
      </TabList>
      <TabContent>{children}</TabContent>
    </TabsContainer>
  );
};

// Simple tabs with content
interface TabPanelProps {
  children: React.ReactNode;
  value: string;
  activeValue: string;
}

export const TabPanel: React.FC<TabPanelProps> = ({ children, value, activeValue }) => {
  if (value !== activeValue) return null;
  return <>{children}</>;
};

